/**
 * Centralized experiment flag registry.
 *
 * Every A/B test flag key lives here. Components use the flag key constants,
 * never raw strings. When you create a new flag in the Amplitude Experiments
 * dashboard, add its key here and document its purpose + variants.
 *
 * Usage in components:
 *   import { EXPERIMENTS } from '../../config/experiments';
 *   const { value } = useExperiment(EXPERIMENTS.PAYWALL_HEADLINE.key);
 */

export interface ExperimentFlag {
    /** The flag key exactly as defined in the Amplitude Experiments dashboard. */
    key: string;
    /** What the experiment tests — for team documentation. */
    description: string;
    /** The value returned when user is in control group or flag is off. */
    fallback: string;
    /** All possible variant values (including control) for reference. */
    variants: readonly string[];
}

// ── Flag definitions ────────────────────────────────────────────────────────
//
// Steps to add a new experiment:
//  1. Create the flag in Amplitude Experiments dashboard
//  2. Add the key + variants here
//  3. Use useExperiment(EXPERIMENTS.YOUR_FLAG.key) in the component
//  4. Exposure is tracked automatically by the hook
//  5. All analytics events automatically include active variants

export const EXPERIMENTS = {
    /**
     * Paywall headline copy variant.
     * Tests different value propositions on the paywall hero section.
     */
    PAYWALL_HEADLINE: {
        key: 'funnel-paywall-headline',
        description: 'Paywall hero headline — test different value props',
        fallback: 'control',
        variants: ['control', 'social_proof', 'urgency', 'personalized'],
    },

    /**
     * Paywall pricing layout.
     * Tests which plan is pre-selected and how plans are displayed.
     */
    PAYWALL_PRICING: {
        key: 'funnel-paywall-pricing',
        description: 'Pricing card layout and default selection',
        fallback: 'control',
        variants: ['control', 'anchored_high', 'trial_first'],
    },

    /**
     * Email capture CTA text.
     * Tests the button/headline text on the email capture step.
     */
    EMAIL_CAPTURE_CTA: {
        key: 'funnel-email-cta',
        description: 'Email capture headline and button copy',
        fallback: 'control',
        variants: ['control', 'curiosity', 'urgency'],
    },

    /**
     * Results page social proof style.
     * Tests different social proof presentations on the results page.
     */
    RESULTS_SOCIAL_PROOF: {
        key: 'funnel-results-social-proof',
        description: 'Results page social proof format',
        fallback: 'control',
        variants: ['control', 'counter', 'testimonial_top'],
    },

    /**
     * Coaching upsell price point.
     * Tests different price points for the coaching add-on.
     */
    COACHING_UPSELL_PRICE: {
        key: 'funnel-coaching-price',
        description: 'Coaching upsell price point test',
        fallback: 'control',
        variants: ['control', 'lower', 'anchored'],
    },
} as const satisfies Record<string, ExperimentFlag>;

// Type-safe flag key union
export type ExperimentKey = typeof EXPERIMENTS[keyof typeof EXPERIMENTS]['key'];

// All flag keys as an array (useful for iterating)
export const ALL_EXPERIMENT_KEYS: ExperimentKey[] = Object.values(EXPERIMENTS).map(f => f.key);
